import { useQuery } from '@tanstack/react-query'
import { ColumnDef } from '@tanstack/react-table'
import { OverflownTextTooltip } from 'components/OverflownTextTooltip'
import { useCallback, useMemo, useState } from 'react'
import { DataTable } from '../../../components/DataTable'
import { DataTableContainer } from '../../../components/DataTable/UI'
import * as SettingsS from './styles'
import * as S from '../../../components/Tables/CommonTable.styles'
import { TextPill } from 'components/FbUI/TextPill'
import {
  ActionsDropdown,
  createEditAction,
  createCopyAction,
  createToggleActiveAction,
  createDeleteAction,
} from 'components/Buttons/ActionsDropdown'
import FbButton from 'components/FbUI/FbButton'
import { CreateAutomationModal } from './AutomationModal/CreateAutomationModal'
import { EditAutomationModal } from './AutomationModal/EditAutomationModal'
import { CopyAutomationModal } from './AutomationModal/CopyAutomationModal'
import { Automation } from 'models/automation'
import apiService from 'services/api'
import { toast } from 'react-toastify'
import confirm from 'components/dialogConfirm'
import { DataTableProvider } from 'components/DataTable/DataTableContext'
import { ColumnsStoreProvider } from 'stores/ColumnsStore/ColumnsStoreProvider'
import { dataTableSortingStoreRepo } from 'components/DataTable/DataTableSorting/DataTableSortingStore'
import {
  filterStoreRepo,
  useFilterParams,
} from 'components/Filters/FilterStore'
import { TableSearch } from 'components/Filters/TableSearch'
import { FilterIdentifier } from 'models/saved_view'

const PAGE_SIZE = 20
const TABLE_KEY = 'automations-table'
const FILTER_IDENTIFIER: FilterIdentifier = 'AUTOMATIONS_TABLE'
const DESCRIPTION_TEXT =
  'Manage your business rule automations. Create, edit, and monitor automated actions that trigger based on deal events and conditions.'

export function AutomationsTable() {
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingAutomationId, setEditingAutomationId] = useState<number | null>(
    null
  )
  const [copyingAutomationId, setCopyingAutomationId] = useState<number | null>(
    null
  )

  const api = apiService()
  const automationsFilterStore = filterStoreRepo.getStore(FILTER_IDENTIFIER)
  const { page, setPage } = automationsFilterStore()
  const { sorting } = dataTableSortingStoreRepo.getStore(TABLE_KEY)()

  const filtersAsParams = useFilterParams(automationsFilterStore)

  // Build sort params
  const sortParams = useMemo(() => {
    const params: Record<string, unknown> = {}
    if (sorting?.length) {
      params['sort'] = sorting[0].desc ? '-' + sorting[0]?.id : sorting[0]?.id
    }
    return params
  }, [sorting])

  const filterAndSortParams = useMemo(
    () => ({
      ...filtersAsParams,
      ...sortParams,
    }),
    [filtersAsParams, sortParams]
  )

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['automations', page, filterAndSortParams],
    queryFn: async ({ signal }) => {
      return await api.getAutomations(
        {
          page,
          limit: PAGE_SIZE,
          ...filterAndSortParams,
        },
        signal
      )
    },
  })

  const handleDelete = useCallback(
    async (automationId: number) => {
      const confirmed = await confirm(
        'Are you sure you want to delete this automation?',
        'Delete Automation',
        'This action cannot be undone.',
        { confirmLabel: 'Delete', buttonVariant: 'danger' }
      )

      if (!confirmed) {
        return
      }

      try {
        await api.deleteAutomation(automationId)
        toast.success('Automation deleted successfully')
        refetch()
      } catch (err) {
        console.error('Failed to delete automation:', err)
        toast.error('Failed to delete automation')
      }
    },
    [api, refetch]
  )

  const handleToggleActive = useCallback(
    async (automationId: number, currentActiveState: boolean) => {
      try {
        await api.partialUpdateAutomation(automationId, {
          is_active: !currentActiveState,
        })
        toast.success(
          `Automation ${!currentActiveState ? 'enabled' : 'disabled'} successfully`
        )
        refetch()
      } catch (err) {
        console.error('Failed to update automation:', err)
        toast.error('Failed to update automation')
      }
    },
    [api, refetch]
  )

  const columns = useMemo<ColumnDef<Automation, any>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Automation Name',
        size: 300,
      },
      {
        accessorKey: 'description',
        header: 'Description',
        size: 200,
      },
      {
        accessorKey: 'is_active',
        header: 'Status',
        size: 150,
        cell: (info) => {
          if (info.getValue()) {
            return (
              <TextPill textColor="#13462C" backgroundColor="#D1E8D5">
                Active
              </TextPill>
            )
          } else {
            return (
              <TextPill textColor="#464646" backgroundColor="#E3E1DA">
                Disabled
              </TextPill>
            )
          }
        },
      },
      {
        accessorFn: (row) =>
          `${row.created_by.first_name} ${row.created_by.last_name}`,
        header: 'Created By',
        size: 150,
      },
      {
        accessorKey: 'created',
        header: 'Date Created',
        size: 150,
        cell: (info) => {
          const date = new Date(info.getValue())
          return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })
        },
      },
      {
        id: 'actions',
        header: 'Actions',
        size: 100,
        meta: {
          rightAlign: true,
        },
        cell: (info) => (
          <ActionsDropdown
            actions={[
              createEditAction(() => {
                setEditingAutomationId(info.row.original.id)
              }),
              createCopyAction(() => {
                setCopyingAutomationId(info.row.original.id)
              }),
              createToggleActiveAction(
                () =>
                  handleToggleActive(
                    info.row.original.id,
                    info.row.original.is_active
                  ),
                info.row.original.is_active
              ),
              createDeleteAction(() => {
                handleDelete(info.row.original.id)
              }),
            ]}
          />
        ),
      },
    ],
    [handleDelete, handleToggleActive]
  )

  return (
    <DataTableContainer>
      <ColumnsStoreProvider tableKey={TABLE_KEY}>
        <DataTableProvider tableKey={TABLE_KEY}>
          <DataTable
            tableKey={TABLE_KEY}
            tableHeader={
              <>
                <SettingsS.SettingsTableHeader>
                  <OverflownTextTooltip
                    tooltipText={DESCRIPTION_TEXT}
                    maxLines={4}
                    placement="bottom"
                  >
                    <SettingsS.TableDescriptionText>
                      {DESCRIPTION_TEXT}
                    </SettingsS.TableDescriptionText>
                  </OverflownTextTooltip>
                </SettingsS.SettingsTableHeader>
                <S.SearchContainer>
                  <S.SearchContainerFilters>
                    <S.TableSearchContainer>
                      <TableSearch
                        filterStore={automationsFilterStore}
                        searchPlaceholder="Search automations..."
                        tableKey={TABLE_KEY}
                      />
                    </S.TableSearchContainer>
                    <S.TableButtonsContainer>
                      <FbButton
                        variant="primary"
                        size="sm"
                        onClick={() => setShowCreateModal(true)}
                      >
                        Create Automation
                      </FbButton>
                    </S.TableButtonsContainer>
                  </S.SearchContainerFilters>
                </S.SearchContainer>
              </>
            }
            height={600}
            data={data?.results || []}
            columns={columns}
            loading={isLoading || isFetching}
            isPaginationEnabled={true}
            paginationOptions={{
              page: page,
              totalRows: data?.count || 0,
              pageSize: PAGE_SIZE,
              setPage: setPage,
              isPaginationLoading: isFetching,
            }}
            sortableFields="all"
            virtualizeRows={false}
          />
        </DataTableProvider>
      </ColumnsStoreProvider>

      <CreateAutomationModal
        show={showCreateModal}
        handleClose={() => setShowCreateModal(false)}
        onAutomationCreated={refetch}
      />

      {editingAutomationId && (
        <EditAutomationModal
          show={!!editingAutomationId}
          handleClose={() => setEditingAutomationId(null)}
          automationId={editingAutomationId}
          onAutomationUpdated={refetch}
        />
      )}

      {copyingAutomationId && (
        <CopyAutomationModal
          show={!!copyingAutomationId}
          handleClose={() => setCopyingAutomationId(null)}
          automationId={copyingAutomationId}
          onAutomationCreated={refetch}
        />
      )}
    </DataTableContainer>
  )
}
